import type { ReactNode } from 'react';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import styles from './PullToRefresh.module.css';

interface PullToRefreshProps {
  /** Reload the screen's data. The spinner keeps spinning until this settles. */
  onRefresh: () => Promise<unknown> | void;
  children: ReactNode;
}

const RADIUS = 9;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** Fraction of the ring drawn when fully engaged (a 3/4 arc, iOS-style). */
const ARC = 0.75;

/**
 * Wraps a screen's scrollable content with a native pull-to-refresh gesture and
 * an iOS-style circular spinner. On web / non-native platforms it renders its
 * children unchanged (the gesture is disabled).
 */
export function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const { containerRef, pull, refreshing, dragging, progress, enabled } =
    usePullToRefresh(onRefresh);

  if (!enabled) return <>{children}</>;

  const spinnerVisible = pull > 0 || refreshing;
  const contentTransition = dragging
    ? 'none'
    : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
  const dashoffset = refreshing
    ? CIRCUMFERENCE * (1 - ARC)
    : CIRCUMFERENCE * (1 - progress * ARC);

  return (
    <div ref={containerRef} className={styles.container}>
      <div
        className={styles.spinnerWrap}
        style={{ transform: `translateY(${pull}px)`, opacity: spinnerVisible ? 1 : 0 }}
        aria-hidden={!spinnerVisible}
      >
        <div
          className={`${styles.spinner} ${refreshing ? styles.spinning : ''}`}
          style={{
            opacity: refreshing ? 1 : progress,
            transform: `scale(${refreshing ? 1 : 0.6 + progress * 0.4}) rotate(${
              refreshing ? 0 : progress * 270
            }deg)`,
          }}
          role={refreshing ? 'progressbar' : undefined}
          aria-label={refreshing ? 'Refreshing' : undefined}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle
              cx="12"
              cy="12"
              r={RADIUS}
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashoffset}
              transform="rotate(-90 12 12)"
            />
          </svg>
        </div>
      </div>

      <div
        className={styles.content}
        style={{ transform: `translateY(${pull}px)`, transition: contentTransition }}
      >
        {children}
      </div>
    </div>
  );
}
